// Framing math for the export preview — the same rectangle the worker crops
// (emo/transcript/reframe.py view_crop), so what you frame in the preview is what you get.
//
// A view is { x, y, zoom, face }: x/y = the centre of what you see as a fraction of the
// SOURCE frame, zoom 1..3, face = "aim at the speaker" (the worker finds the face).

export const MAX_ZOOM = 3;
export const DEFAULT_VIEW = { x: 0.5, y: 0.5, zoom: 1, face: false };

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
// A number, or the default (0 is a real position — `|| 0.5` would lose it).
const num = (v, d) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? d : Number(v));

/** What each shape's output frame is: its aspect, and the aspect of the picture inside it. */
export const shapeGeometry = (shape, sourceAspect) => {
  if (shape === 'zoom') return { output: 9 / 16, picture: 9 / 16 };
  if (shape === 'blur') return { output: 9 / 16, picture: sourceAspect };
  if (shape === 'landscape') return { output: 16 / 9, picture: sourceAspect };
  return { output: sourceAspect, picture: sourceAspect }; // original
};

/** The part of the source a view shows: { w, h, left, top } as fractions of the source. */
export const cropWindow = (sourceAspect, pictureAspect, view) => {
  const v = view || DEFAULT_VIEW;
  const zoom = clamp(num(v.zoom, 1), 1, MAX_ZOOM);
  let bw = 1;
  let bh = 1;
  if (sourceAspect > pictureAspect) bw = pictureAspect / sourceAspect;
  else bh = sourceAspect / pictureAspect;
  const w = bw / zoom;
  const h = bh / zoom;
  const left = clamp(num(v.x, 0.5) - w / 2, 0, 1 - w);
  const top = clamp(num(v.y, 0.5) - h / 2, 0, 1 - h);
  return { w, h, left, top };
};

/** CSS (percentages of the picture box) that shows that window of the image. */
export const imageStyle = (win) => ({
  position: 'absolute',
  width: `${100 / win.w}%`,
  height: `${100 / win.h}%`,
  left: `${(-win.left / win.w) * 100}%`,
  top: `${(-win.top / win.h) * 100}%`,
  maxWidth: 'none',
});

/** Pan by a drag of (dxFrac, dyFrac) of the picture box; returns the new view (face off). */
export const panView = (view, win, dxFrac, dyFrac) => {
  const v = view || DEFAULT_VIEW;
  const x = clamp(num(v.x, 0.5) - dxFrac * win.w, win.w / 2, 1 - win.w / 2);
  const y = clamp(num(v.y, 0.5) - dyFrac * win.h, win.h / 2, 1 - win.h / 2);
  return { ...v, x, y, face: false };
};

/** A layout is "customised" when it moves the picture or pins its own caption. */
export const isCustomLayout = (layout) =>
  Boolean(layout && ((layout.view && (layout.view.zoom > 1 || layout.view.face
    || Math.abs(layout.view.x - 0.5) > 0.001 || Math.abs(layout.view.y - 0.5) > 0.001)) || layout.caption));
